import "antd/dist/antd.css";
import "../App.css";

import GuideArticle from "../components/GuideArticle";
import GuideSection from "../components/GuideSection";
import GuideStackLead from "../components/GuideStackLead";
import GuideCtaBand from "../components/GuideCtaBand";
import CtaCard from "../components/CtaCard";
import RelatedGuides from "../components/RelatedGuides";
import usePageMeta from "../usePageMeta";
import { GUIDES } from "../data/guides";

const META = GUIDES.find((g) => g.slug === "power-plugs")!;

// Regional accent palette used for the plug SVG fill AND the voltage
// badge. Same colour ties an icon to its region visually — the wall of
// nine cards stops being a monotone slab.
const ACCENTS: Record<string, { fill: string; badgeBg: string; badgeText: string }> = {
    NA: { fill: "#b45309", badgeBg: "#fef3c7", badgeText: "#78350f" }, // North America — amber
    EU: { fill: "#1e3a8a", badgeBg: "#dbeafe", badgeText: "#1e3a8a" }, // Continental Europe — blue
    UK: { fill: "#991b1b", badgeBg: "#fee2e2", badgeText: "#7f1d1d" }, // UK / Ireland — red
    CH: { fill: "#7c2d12", badgeBg: "#ffedd5", badgeText: "#7c2d12" }, // Switzerland — burnt orange
    IT: { fill: "#065f46", badgeBg: "#d1fae5", badgeText: "#064e3b" }, // Italy — deep green
    AU: { fill: "#c2410c", badgeBg: "#ffedd5", badgeText: "#7c2d12" }, // Oceania / China — orange
    JP: { fill: "#0f766e", badgeBg: "#ccfbf1", badgeText: "#134e4a" }, // Japan — teal
    BR: { fill: "#166534", badgeBg: "#dcfce7", badgeText: "#14532d" }, // Brazil — leaf green
};

// Each plug-face icon is drawn 56×56 in a flat single-colour style so the
// shape reads at a glance. Stylised, not to scale.
const PlugSVG: Record<string, (fill: string) => React.ReactNode> = {
    A: (fill) => (
        <svg viewBox="0 0 56 56" width="56" height="56" aria-hidden="true">
            <rect x="6" y="6" width="44" height="44" rx="22" fill={fill} />
            <rect x="20" y="16" width="3" height="20" fill="#fff" />
            <rect x="33" y="16" width="3" height="20" fill="#fff" />
        </svg>
    ),
    B: (fill) => (
        <svg viewBox="0 0 56 56" width="56" height="56" aria-hidden="true">
            <rect x="6" y="6" width="44" height="44" rx="22" fill={fill} />
            <rect x="20" y="14" width="3" height="18" fill="#fff" />
            <rect x="33" y="14" width="3" height="18" fill="#fff" />
            <circle cx="28" cy="40" r="3" fill="#fff" />
        </svg>
    ),
    C: (fill) => (
        <svg viewBox="0 0 56 56" width="56" height="56" aria-hidden="true">
            <rect x="6" y="6" width="44" height="44" rx="22" fill={fill} />
            <circle cx="22" cy="28" r="3.5" fill="#fff" />
            <circle cx="34" cy="28" r="3.5" fill="#fff" />
        </svg>
    ),
    F: (fill) => (
        <svg viewBox="0 0 56 56" width="56" height="56" aria-hidden="true">
            <rect x="6" y="6" width="44" height="44" rx="22" fill={fill} />
            <circle cx="22" cy="28" r="4" fill="#fff" />
            <circle cx="34" cy="28" r="4" fill="#fff" />
            <rect x="14" y="11" width="28" height="3" fill="#fff" />
            <rect x="14" y="42" width="28" height="3" fill="#fff" />
        </svg>
    ),
    G: (fill) => (
        <svg viewBox="0 0 56 56" width="56" height="56" aria-hidden="true">
            <rect x="6" y="6" width="44" height="44" rx="10" fill={fill} />
            <rect x="26.5" y="11" width="3" height="14" fill="#fff" />
            <rect x="13" y="32" width="3" height="14" fill="#fff" transform="rotate(-90 14.5 39)" />
            <rect x="40" y="32" width="3" height="14" fill="#fff" transform="rotate(90 41.5 39)" />
        </svg>
    ),
    I: (fill) => (
        <svg viewBox="0 0 56 56" width="56" height="56" aria-hidden="true">
            <rect x="6" y="6" width="44" height="44" rx="22" fill={fill} />
            {/* Two angled flat pins forming an inverted V */}
            <rect x="19" y="14" width="3" height="14" fill="#fff" transform="rotate(20 20.5 21)" />
            <rect x="34" y="14" width="3" height="14" fill="#fff" transform="rotate(-20 35.5 21)" />
            <rect x="26.5" y="34" width="3" height="10" fill="#fff" />
        </svg>
    ),
    J: (fill) => (
        <svg viewBox="0 0 56 56" width="56" height="56" aria-hidden="true">
            <rect x="6" y="6" width="44" height="44" rx="22" fill={fill} />
            {/* Three round pins in a wide triangle (offset earth) */}
            <circle cx="20" cy="22" r="3.5" fill="#fff" />
            <circle cx="36" cy="22" r="3.5" fill="#fff" />
            <circle cx="28" cy="38" r="3.5" fill="#fff" />
        </svg>
    ),
    L: (fill) => (
        <svg viewBox="0 0 56 56" width="56" height="56" aria-hidden="true">
            <rect x="6" y="6" width="44" height="44" rx="22" fill={fill} />
            {/* Three round pins in a horizontal line, earth in the middle */}
            <circle cx="17" cy="28" r="3.5" fill="#fff" />
            <circle cx="28" cy="28" r="3.5" fill="#fff" />
            <circle cx="39" cy="28" r="3.5" fill="#fff" />
        </svg>
    ),
    N: (fill) => (
        <svg viewBox="0 0 56 56" width="56" height="56" aria-hidden="true">
            <rect x="6" y="6" width="44" height="44" rx="22" fill={fill} />
            {/* Three round pins in a triangle, earth offset to one side */}
            <circle cx="20" cy="34" r="3.5" fill="#fff" />
            <circle cx="36" cy="34" r="3.5" fill="#fff" />
            <circle cx="28" cy="18" r="3.5" fill="#fff" />
        </svg>
    ),
};

type PlugType = {
    letter: keyof typeof PlugSVG;
    name: string;
    accent: keyof typeof ACCENTS;
    voltage: string;
    // Bold at-a-glance summary shown as a coloured badge on the card.
    voltageBadge: string;
    pinSummary: string;
    // Flag-led country chips. `label` prints under the flag when a
    // reader is skimming; the extra prose stays in the main text below.
    flags: { emoji: string; label: string }[];
    countries: string;
    notes?: string;
};

const PLUG_TYPES: PlugType[] = [
    {
        letter: "A",
        name: "Type A — North American flat blades",
        accent: "NA",
        voltage: "120V, 60Hz",
        voltageBadge: "120V · 60Hz",
        pinSummary:
            "Two parallel flat blades, ungrounded. One blade slightly wider than the other on modern plugs (polarised).",
        flags: [
            { emoji: "🇺🇸", label: "USA" },
            { emoji: "🇨🇦", label: "Canada" },
            { emoji: "🇲🇽", label: "Mexico" },
            { emoji: "🇯🇵", label: "Japan" },
        ],
        countries:
            "Most of Central America, parts of South America (Colombia, Venezuela, Ecuador), Japan (100V/50Hz or 60Hz depending on region).",
        notes:
            "Often interchangeable with Type B sockets. Older sockets accept Type A only; modern ones accept both.",
    },
    {
        letter: "B",
        name: "Type B — North American grounded",
        accent: "NA",
        voltage: "120V, 60Hz",
        voltageBadge: "120V · 60Hz",
        pinSummary:
            "Two flat blades plus a round earth pin below, forming a 'face' shape. Earth pin protrudes ~3mm beyond the blades so it makes contact first.",
        flags: [
            { emoji: "🇺🇸", label: "USA" },
            { emoji: "🇨🇦", label: "Canada" },
            { emoji: "🇲🇽", label: "Mexico" },
            { emoji: "🇯🇵", label: "Japan" },
        ],
        countries:
            "Increasingly common in Central America for high-draw appliances. Japan is rarer than Type A.",
        notes:
            "All laptop chargers and most modern electronics use Type B. A Type A travel adapter often won't fit a Type B brick.",
    },
    {
        letter: "C",
        name: "Type C — Europlug",
        accent: "EU",
        voltage: "220–240V, 50Hz",
        voltageBadge: "230V · 50Hz",
        pinSummary:
            "Two round 4mm pins, 19mm apart, ungrounded. Rated max 2.5A — the small thin plug you see on phone chargers and lamps.",
        flags: [
            { emoji: "🇫🇷", label: "France" },
            { emoji: "🇩🇪", label: "Germany" },
            { emoji: "🇪🇸", label: "Spain" },
            { emoji: "🇮🇹", label: "Italy" },
            { emoji: "🇳🇱", label: "Netherlands" },
        ],
        countries:
            "Nearly all of continental Europe, plus parts of South America, Asia, Russia and the Middle East. Universally accepted as a 'fits in any European socket' standard for low-draw devices.",
        notes:
            "Type C plugs fit into Type E, F, J, K, L and N sockets. The thing every European phone charger has by default.",
    },
    {
        letter: "F",
        name: "Type F — Schuko",
        accent: "EU",
        voltage: "230V, 50Hz",
        voltageBadge: "230V · 50Hz",
        pinSummary:
            "Two round 4.8mm pins, 19mm apart, with earth contacts as clips on the top and bottom edges. Plug is recessed into the socket.",
        flags: [
            { emoji: "🇩🇪", label: "Germany" },
            { emoji: "🇦🇹", label: "Austria" },
            { emoji: "🇳🇱", label: "Netherlands" },
            { emoji: "🇸🇪", label: "Sweden" },
            { emoji: "🇪🇸", label: "Spain" },
        ],
        countries:
            "Norway, Finland, Portugal, Greece, Russia, Turkey, most of Eastern Europe.",
        notes:
            "Functionally the European standard for any device that draws more than ~2.5A. A Type C plug fits a Type F socket; a Type F plug doesn't fit a Type C socket.",
    },
    {
        letter: "G",
        name: "Type G — British 13A",
        accent: "UK",
        voltage: "230V, 50Hz",
        voltageBadge: "230V · 50Hz",
        pinSummary:
            "Three rectangular flat pins in a triangle: live and neutral on the bottom, longer earth pin on top. Plug includes a replaceable fuse (usually 3A or 13A).",
        flags: [
            { emoji: "🇬🇧", label: "UK" },
            { emoji: "🇮🇪", label: "Ireland" },
            { emoji: "🇲🇹", label: "Malta" },
            { emoji: "🇸🇬", label: "Singapore" },
            { emoji: "🇭🇰", label: "Hong Kong" },
        ],
        countries:
            "Cyprus, Malaysia, Gibraltar, the UAE (alongside Type C/F).",
        notes:
            "The chunkiest and arguably safest domestic plug in the world. The earth pin opens shutters over live and neutral — kids can't poke things in.",
    },
    {
        letter: "I",
        name: "Type I — Australian / Chinese",
        accent: "AU",
        voltage: "230V, 50Hz (Australia / NZ); 220V, 50Hz (China)",
        voltageBadge: "230V · 50Hz",
        pinSummary:
            "Two flat pins at an angle (inverted V), plus a vertical flat earth pin below. Looks like an angry face.",
        flags: [
            { emoji: "🇦🇺", label: "Australia" },
            { emoji: "🇳🇿", label: "New Zealand" },
            { emoji: "🇨🇳", label: "China" },
            { emoji: "🇦🇷", label: "Argentina" },
        ],
        countries:
            "Fiji and PNG also use it. Argentina reverses live and neutral.",
        notes:
            "Australia, NZ and China use the same plug shape — but Australian appliances are NOT certified for use in China and vice versa, because the safety/compliance regimes differ.",
    },
    {
        letter: "J",
        name: "Type J — Swiss",
        accent: "CH",
        voltage: "230V, 50Hz",
        voltageBadge: "230V · 50Hz",
        pinSummary:
            "Three round 4mm pins in a wide offset triangle — two close together for live/neutral, earth pin sits ~5mm to the side and is offset from centre.",
        flags: [
            { emoji: "🇨🇭", label: "Switzerland" },
            { emoji: "🇱🇮", label: "Liechtenstein" },
        ],
        countries:
            "Rwanda and the Maldives too (alongside Type D/G).",
        notes:
            "Almost-but-not-quite a Type C — a Type C plug fits a Type J socket, but a Type J plug won't fit anything else in Europe. Switzerland is the one alpine country where you'll want a proper adapter.",
    },
    {
        letter: "L",
        name: "Type L — Italian",
        accent: "IT",
        voltage: "230V, 50Hz",
        voltageBadge: "230V · 50Hz",
        pinSummary:
            "Three round pins in a horizontal line, earth in the middle. Comes in 10A (small, 4mm pins) and 16A (large, 5mm pins) variants — they're not interchangeable.",
        flags: [
            { emoji: "🇮🇹", label: "Italy" },
            { emoji: "🇨🇱", label: "Chile" },
            { emoji: "🇺🇾", label: "Uruguay" },
        ],
        countries:
            "Parts of North Africa (Libya, Ethiopia) too.",
        notes:
            "Modern Italian buildings increasingly use 'Schuko-compatible' sockets that accept Type C, F, and L. Older buildings have Type L only. Hotels almost always have both.",
    },
    {
        letter: "N",
        name: "Type N — Brazilian",
        accent: "BR",
        voltage: "127V or 220V, 60Hz",
        voltageBadge: "127V / 220V",
        pinSummary:
            "Three round pins in a triangle — two for live/neutral (4mm), earth pin in the middle and slightly higher. Almost identical to Type J but with the pins closer together.",
        flags: [
            { emoji: "🇧🇷", label: "Brazil" },
            { emoji: "🇿🇦", label: "South Africa" },
        ],
        countries:
            "South Africa uses it on newer installations.",
        notes:
            "Brazil is split — north and northeast typically 127V, south typically 220V. Check the socket label at the hotel. A dual-voltage charger handles both; a kettle or hairdryer doesn't.",
    },
];

type RegionGuide = {
    flag: string;
    region: string;
    body: string;
};

const REGIONS: RegionGuide[] = [
    {
        flag: "🇺🇸",
        region: "North America (US, Canada, Mexico)",
        body:
            "Types A and B. 120V at 60Hz. Bring a UK→US adapter and check every device for '100–240V' on the brick. A British kettle plugged in via adapter to 120V will boil at quarter speed but still work. A British hairdryer often won't power on. Power strips with USB ports are universal in airports and hotels.",
    },
    {
        flag: "🇪🇺",
        region: "Western Europe (France, Germany, Spain, Netherlands…)",
        body:
            "Types C and F. 230V at 50Hz. UK plugs need an adapter (UK→EU 'tourist' adapter, the cheap ones are everywhere in airports). A Schuko-style adapter is better than a Europlug-only one for higher-draw devices like laptops. France, Belgium, Czechia, Slovakia and Poland also use Type E sockets which take both C and F plugs.",
    },
    {
        flag: "🇬🇧",
        region: "UK and Ireland",
        body:
            "Type G. 230V at 50Hz. The chunky three-pin with the fuse. If you're coming from Europe, a Schuko→UK adapter is essential and absurdly bulky. Universal adapters with a built-in fuse (e.g. SKROSS, Mu Lite) are worth the extra money.",
    },
    {
        flag: "🇨🇭",
        region: "Switzerland",
        body:
            "Type J. 230V at 50Hz. Looks like a Type C but with an offset earth — a standard EU plug won't fit a Type J socket. Hotels often have at least one universal socket, but if you're staying in alpine refuges, pack a UK→Swiss or EU→Swiss adapter.",
    },
    {
        flag: "🇮🇹",
        region: "Italy",
        body:
            "Type L (and increasingly Type F). 230V at 50Hz. Modern hotels have hybrid sockets that take C/F/L. Older hotels and apartments may be Type L only. The Italian 10A and 16A variants of Type L are NOT interchangeable — the 16A is for ovens and air-con, not chargers.",
    },
    {
        flag: "🇦🇺",
        region: "Australia & New Zealand",
        body:
            "Type I. 230V at 50Hz (Australia) / 230V at 50Hz (NZ). Same plug as China but the appliances aren't legally interchangeable. Sockets often have a built-in switch — flip it on or your charger does nothing.",
    },
    {
        flag: "🇯🇵",
        region: "Japan",
        body:
            "Types A and B. 100V — the lowest standard voltage in the world. Frequency splits east/west: 50Hz in Tokyo and the north, 60Hz in Osaka and the south. Most modern electronics tolerate both, but anything with a motor (electric razor, certain old hairdryers) might run wrong-speed on the 'other' side of the country.",
    },
    {
        flag: "🇧🇷",
        region: "Brazil",
        body:
            "Type N. Voltage is the trap: 127V in the north and northeast (Rio, Salvador, Recife, most of the Amazon), 220V in the south (São Paulo down to Argentina). Each socket should be labelled — read it. Mid-tier hotels often have both.",
    },
];

type Section = {
    heading: string;
    intro?: string;
    items: { title: string; body: string }[];
};

const PRACTICAL: Section[] = [
    {
        heading: "What to actually pack",
        intro:
            "For European-only tours, one UK→Schuko (or vice versa) adapter does the job. For anything further afield, a single universal adapter saves the day.",
        items: [
            {
                title: "Universal travel adapter",
                body:
                    "Mu Lite (sliding-pin design, pocket-sized) or Mogics Donut (built-in USB ports and a small ring of outlets) are the two cult picks. Avoid the cheapest Amazon adapters — counterfeits are common and the live pin can slip back into the body. Spend £20–30 once.",
            },
            {
                title: "USB-C PD wall brick",
                body:
                    "65W minimum if you charge a laptop, 30W if you only do phones and intercoms. A single GaN brick (Anker 736, UGREEN Nexode 65W) covers everything from a phone to a MacBook Pro. Use the local socket via your travel adapter.",
            },
            {
                title: "Bike-side 12V → USB",
                body:
                    "A hardwired 12V→USB-C charger on the bike (Quad Lock dual-port, SAE-to-USB pigtail off the battery) keeps phones, GPS units and Bluetooth intercoms topped up while you ride. Most touring kit never sees a wall socket — only the hotel evening does.",
            },
            {
                title: "What to leave at home",
                body:
                    "Hairdryers, kettles, electric razors that aren't dual-voltage. Anything labelled '120V only' on the brick will buzz, smoke or pop a fuse on European 230V. Anything labelled '230V only' will run at a quarter-power on US 120V. Read the small print on the brick before packing.",
            },
        ],
    },
    {
        heading: "Voltage vs frequency — the actual risk",
        items: [
            {
                title: "The brick label",
                body:
                    "Look on the small text of any charger: '100–240V, 50/60Hz' means it works everywhere. '230V, 50Hz only' means do NOT plug into a US socket. '120V, 60Hz only' means do NOT plug into a European socket. Almost every modern phone, laptop, camera and bike-comms charger is dual-voltage. Almost no kitchen appliance is.",
            },
            {
                title: "Why frequency rarely matters",
                body:
                    "50Hz vs 60Hz only matters for AC motors (older fans, some electric razors, mechanical clocks) and certain transformers. Modern switched-mode power supplies — anything with a small black brick on the cable — don't care. The brand-new e-bike charger or laptop PSU just sees DC after rectification.",
            },
            {
                title: "What a step-up/step-down converter is",
                body:
                    "A heavy box that converts 120V→230V or 230V→120V. Required if you're insistent on bringing a 120V kettle to Europe. Heavy, expensive, often poor quality. Almost always cheaper to buy the equivalent appliance at the destination.",
            },
        ],
    },
];

function PowerPlugsGuide() {
    usePageMeta({
        title:
            "Power plugs around the world — voltages, pin layouts and adapters | Planamoto",
        description:
            "A practical reference to the plug types, voltages and frequencies you'll meet on a multi-country tour, plus what adapter and chargers to actually pack. Focused on the plug types riders and cyclists actually encounter abroad.",
        url: "https://www.planamoto.com/guides/power-plugs",
    });

    return (
        <GuideArticle
            eyebrow="Guide"
            title="Power plugs around the world — voltages, pin layouts and what adapters to pack"
            lede="Phones, GPS units, intercoms, e-bike batteries, laptops — every multi-country tour leaves a trail of devices looking for a socket. The plug fit is rarely the problem; the voltage and frequency are. This is a practical reference to the plug types riders and cyclists actually meet on a tour, plus what to pack so the only thing that fails in your hotel is the Wi-Fi."
            heroImage={{
                src: META.image!.src,
                alt: META.image!.alt ?? "Travel adapter and USB-C wall charger on a hotel desk",
            }}
            beforeSections={
                <>
                    <GuideStackLead
                        eyebrow="What I actually pack"
                        authorNote="Spencer · three items, every trip"
                        heading="Three things cover every socket I've met"
                        intro="The full plug-and-voltage reference is below, but on tour it comes down to three items and one rule: check the brick says 100–240V before it goes near a socket."
                        columns={[
                            {
                                label: "01 · Adapter",
                                title: "One universal adapter",
                                body: "Mu Lite or Mogics Donut. Skip the cheapest Amazon ones — counterfeits let the live pin slip back into the body. Spend £20–30 once.",
                            },
                            {
                                label: "02 · Brick",
                                title: "A 65W GaN brick",
                                body: "Anker 736 or UGREEN Nexode 65W — phone to MacBook off one plug, via the travel adapter. 30W if it's only phones and intercoms.",
                            },
                            {
                                label: "03 · On the bike",
                                title: "A 12V→USB-C feed",
                                body: "Hardwired to the bike (Quad Lock dual-port, SAE pigtail). Most touring kit only sees a wall socket in the hotel evening.",
                            },
                        ]}
                        backstop="read the small text on the brick. '100–240V' works everywhere; '120V only' dies on European 230V; '230V only' crawls at quarter-power on US 120V. Leave the kettle, hairdryer and non-dual-voltage razor at home."
                    />
                    <div className="gt-guide-transition">
                        <div className="gt-eyebrow gt-guide-transition__eyebrow">
                            The full reference
                        </div>
                        <h2 className="gt-guide-transition__heading">
                            Every plug you'll actually meet, and where.
                        </h2>
                        <p className="gt-guide-transition__intro">
                            The IEC defines 14 plug types (A–O). For touring you'll
                            meet maybe nine — the rest your universal adapter has
                            anyway.
                        </p>
                    </div>
                </>
            }
            articleSchema={{
                slug: META.slug,
                datePublished: META.datePublished,
                dateModified: META.dateModified,
                image: META.image?.src,
                description: META.summary,
            }}
        >
            <section className="gt-guide-feature gt-guide-feature--cream">
                <div className="gt-guide-feature__inner">
                    <div className="gt-plug-facts">
                        <div className="gt-plug-facts__item">
                            <strong>9</strong> plug types
                        </div>
                        <div className="gt-plug-facts__item">
                            <strong>3</strong> voltage bands
                        </div>
                        <div className="gt-plug-facts__item">
                            <strong>2</strong> frequencies
                        </div>
                    </div>
                    <div className="gt-plug-grid">
                        {PLUG_TYPES.map((p) => {
                            const accent = ACCENTS[p.accent];
                            return (
                                <article
                                    key={p.letter}
                                    className="gt-card gt-plug-card"
                                    style={{ borderTop: `4px solid ${accent.fill}` }}
                                >
                                    <div className="gt-plug-card__header">
                                        <div className="gt-plug-card__icon" aria-hidden="true">
                                            {PlugSVG[p.letter](accent.fill)}
                                        </div>
                                        <div className="gt-plug-card__title-block">
                                            <div className="gt-plug-card__letter">
                                                Type {p.letter}
                                            </div>
                                            <span
                                                className="gt-plug-card__badge"
                                                style={{
                                                    background: accent.badgeBg,
                                                    color: accent.badgeText,
                                                }}
                                            >
                                                {p.voltageBadge}
                                            </span>
                                        </div>
                                    </div>
                                    <h3 className="gt-h4 gt-plug-card__name">
                                        {p.name.replace(/^Type [A-Z] — /, "")}
                                    </h3>
                                    <ul
                                        className="gt-plug-card__flags"
                                        aria-label="Where this plug is used"
                                    >
                                        {p.flags.map((f) => (
                                            <li
                                                key={f.label}
                                                className="gt-plug-card__flag"
                                            >
                                                <span
                                                    className="gt-plug-card__flag-emoji"
                                                    aria-hidden="true"
                                                >
                                                    {f.emoji}
                                                </span>
                                                <span className="gt-plug-card__flag-label">
                                                    {f.label}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                    <p className="gt-plug-card__pins">
                                        <strong>Pins:</strong> {p.pinSummary}
                                    </p>
                                    {p.countries && (
                                        <p className="gt-plug-card__where">
                                            <strong>Also:</strong> {p.countries}
                                        </p>
                                    )}
                                    {p.notes && (
                                        <p className="gt-plug-card__notes">
                                            {p.notes}
                                        </p>
                                    )}
                                </article>
                            );
                        })}
                    </div>
                </div>
            </section>

            <GuideSection
                index={1}
                heading="By region"
                intro="The plug and voltage you'll actually hit, region by region — and the one adapter that covers it."
                chipLabel="What to expect on the ground"
                accent={{ fill: "#0f4c5c", chipBg: "rgba(15,76,92,.12)", chipText: "#0f4c5c" }}
                items={REGIONS.map((r) => ({
                    title: r.region,
                    body: r.body,
                    marker: r.flag,
                }))}
            />

            {PRACTICAL.map((s, i) => {
                if (i === 0) {
                    return (
                        <GuideSection
                            key={s.heading}
                            index={2}
                            heading="What to actually pack"
                            intro={s.intro}
                            chipLabel="The kit that covers it"
                            accent={{ fill: "#c1654a", chipBg: "rgba(193,101,74,.13)", chipText: "#a1462c" }}
                            items={s.items.map((it) => ({ ...it, marker: "✓" }))}
                        />
                    );
                }
                return (
                    <GuideSection
                        key={s.heading}
                        index={3}
                        heading={s.heading}
                        intro="The plug fit is the easy part — your adapter handles it. What fries a device is voltage. This is the whole of what actually matters, in three cards."
                        highlight={{ glyph: "★", background: "var(--gt-accent)", color: "#3a2a0a" }}
                        highlightColumns={3}
                        items={s.items.map((it, j) => ({ ...it, marker: String(j + 1) }))}
                    />
                );
            })}

            <GuideCtaBand
                heading="Sort the kit once. We'll handle the route."
                body="Adapter, brick and charger go on your shared packing checklist so nobody's hunting a socket at 11pm. Then clone a hand-curated multi-country tour and get on with the fun part."
            />

            <RelatedGuides
                slugs={[
                    "packing-europe",
                    "cycle-touring-packing",
                    "french-toll-roads",
                ]}
            />
            <CtaCard
                title="Plan the route too"
                body="Once the kit is sorted, the route is the fun part. Browse our hand-curated multi-day tours and clone one as your starting point."
                primary={{ label: "Browse tours", to: "/explore" }}
            />
        </GuideArticle>
    );
}

export default PowerPlugsGuide;
